// SafeListboxPortal.jsx
import React, { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Listbox, Transition } from "@headlessui/react";
import { ChevronDownIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/solid";
import PropTypes from "prop-types";

/**
 * SafeListboxPortal
 * - Renders Listbox.Options into document.body
 * - Positions popup using getBoundingClientRect() (position: fixed)
 * - Keeps width matched to trigger
 * - Updates on open, resize and scroll
 *
 * Props similar to previous SafeListbox (items: [{ name, price, ... }], value, onChange)
 */

const SafeListboxPortal = ({
  items = [],
  value = "",
  onChange,
  placeholder = "Choose...",
  buttonClassName = "",
  optionsClassName = "",
  renderLabel,
  renderMeta,
  ariaLabel = "Select option",
}) => {
  const btnRef = useRef(null);
  const popupRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ left: 0, top: 0, width: "auto" });

  // compute popup position & width from trigger rect
  const compute = () => {
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Use rect values directly (viewport coords). position fixed doesn't need scroll offsets.
    setCoords({
      left: Math.round(rect.left),
      top: Math.round(rect.bottom),
      width: Math.ceil(rect.width) + "px",
    });
  };

  // on open compute, on resize/scroll compute (debounced)
  useEffect(() => {
    if (!open) return;
    compute();
    let tid;
    const handler = () => {
      clearTimeout(tid);
      tid = setTimeout(compute, 60);
    };
    window.addEventListener("resize", handler, { passive: true });
    window.addEventListener("scroll", handler, { passive: true }); // catches page scrolls
    return () => {
      clearTimeout(tid);
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler);
    };
  }, [open]);

  const selectedItem = items.find((it) => (it?.name ?? "") === value);

  // Portal content
  const popup = (
    <div
      ref={popupRef}
      style={{
        position: "fixed",
        left: coords.left,
        top: coords.top,
        width: coords.width,
        zIndex: 99999,
        pointerEvents: "auto",
      }}
      aria-hidden={!open}
    >
      <div className={optionsClassName}>
        {items.map((item) => (
          <div
            key={item.name}
            role="button"
            tabIndex={0}
            onClick={() => {
              onChange(item.name);
              setOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                onChange(item.name);
                setOpen(false);
              }
            }}
            className="cursor-pointer select-none relative px-4 py-2 text-sm text-purple-200 hover:bg-purple-600/30"
            aria-label={item.name}
          >
            <div className="flex items-center justify-between">
              <div className="truncate">{renderLabel ? renderLabel(item) : item.name}</div>
              <div className="ml-3 text-xs text-purple-100">{renderMeta ? renderMeta(item) : item.price ? `₹${Number(item.price).toLocaleString("en-IN")}` : ""}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // If document not ready or server-side, avoid portal
  const canPortal = typeof document !== "undefined" && document.body;

  return (
    <div className="relative">
      <Listbox
        value={value}
        onChange={(v) => {
          onChange(v);
        }}
      >
        <div>
          <Listbox.Button
            ref={btnRef}
            aria-label={ariaLabel}
            onClick={() => {
              // toggle open — compute will run in effect when open becomes true
              setOpen((s) => !s);
              // small compute immediately so popup doesn't jump
              setTimeout(() => compute(), 0);
            }}
            className={`flex items-center justify-between ${buttonClassName}`}
          >
            <span className="truncate">
              {selectedItem ? (renderLabel ? renderLabel(selectedItem) : `${selectedItem.name} ${selectedItem.price ? `— ₹${Number(selectedItem.price).toLocaleString("en-IN")}` : ""}`) : placeholder}
            </span>
            <div className="flex items-center gap-2">
                {/* Clear Option */}
                {value && (
                  <div
                    role="button"
                    title="Clear selection"
                    onClick={(e) => {
                        e.stopPropagation();
                        onChange("");
                        setOpen(false); // Close dropdown if open
                    }}
                    className="p-1 hover:bg-white/10 rounded-full transition-colors cursor-pointer"
                  >
                      <XMarkIcon className="w-4 h-4 text-purple-300" />
                  </div>
                )}
                <ChevronDownIcon className="w-5 h-5 text-purple-200" />
            </div>
          </Listbox.Button>

          {/* Render portal-based popup so it sits on top of all stacking contexts */}
          {canPortal ? createPortal(open ? popup : null, document.body) : open ? popup : null}

          {/* Close popup when clicking outside */}
          {open && (
            <div
              onMouseDown={() => setOpen(false)}
              style={{ position: "fixed", inset: 0, zIndex: 99998, background: "transparent" }}
              aria-hidden
            />
          )}
        </div>
      </Listbox>
    </div>
  );
};

SafeListboxPortal.propTypes = {
  items: PropTypes.array.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  buttonClassName: PropTypes.string,
  optionsClassName: PropTypes.string,
  renderLabel: PropTypes.func,
  renderMeta: PropTypes.func,
  ariaLabel: PropTypes.string,
};

export default SafeListboxPortal;
